import { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import type { Editor } from '@tiptap/core';
import { Bold, Italic, Underline, Strikethrough, Code, List, ListOrdered } from 'lucide-react';
import { Separator } from '@/shared/components/ui/separator';
import { cn } from '@/core/lib/utils';
import { ToolbarButton } from './ToolbarButton';
import { LinkPopover } from './LinkPopover';

interface FixedToolbarProps {
  editor: Editor;
  variant?: 'plain' | 'comment';
}

/**
 * Forces a re-render on every editor transaction so active states stay in sync.
 * Bubble/floating toolbars get this from TipTap's menu plugins; a persistent
 * toolbar must subscribe itself.
 */
function useEditorSync(editor: Editor) {
  const [, bump] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    editor.on('transaction', bump);
    return () => {
      editor.off('transaction', bump);
    };
  }, [editor]);
}

/**
 * Compact, single-row formatting toolbar rendered inside the editor box
 * (ClickUp/shadcn pattern). Intentionally minimal — richer formatting lives in
 * the selection bubble menu and the slash menu. Core marks stay visible on
 * mobile; the rest collapse behind `md:`.
 */
export function FixedToolbar({ editor, variant = 'plain' }: FixedToolbarProps) {
  const { t } = useTranslation('richText');
  useEditorSync(editor);

  return (
    <div
      data-testid="rich-text-fixed-toolbar"
      className={cn(
        'flex items-center gap-0.5 px-1.5 py-1',
        // `comment` sits inside a fully bordered box — the toolbar divider separates
        // it from the text area. `plain` has no surrounding box, so the divider would
        // float with nothing to anchor it to.
        variant === 'comment' && 'border-b border-border/60',
      )}
    >
      <ToolbarButton
        icon={<Bold className="h-4 w-4" />}
        label={t('bubble.bold')}
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        testId="rich-text-fixed-bold"
      />
      <ToolbarButton
        icon={<Italic className="h-4 w-4" />}
        label={t('bubble.italic')}
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        testId="rich-text-fixed-italic"
      />
      <div className="hidden md:flex items-center gap-0.5">
        <ToolbarButton
          icon={<Underline className="h-4 w-4" />}
          label={t('bubble.underline')}
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          testId="rich-text-fixed-underline"
        />
        <ToolbarButton
          icon={<Strikethrough className="h-4 w-4" />}
          label={t('bubble.strike')}
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          testId="rich-text-fixed-strike"
        />
      </div>

      <Separator orientation="vertical" className="mx-1.5 h-4 opacity-40" />

      <ToolbarButton
        icon={<Code className="h-4 w-4" />}
        label={t('bubble.code')}
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
        testId="rich-text-fixed-code"
      />

      <Separator orientation="vertical" className="mx-1.5 h-4 opacity-40" />

      <ToolbarButton
        icon={<List className="h-4 w-4" />}
        label={t('floating.bulletList')}
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        testId="rich-text-fixed-bullet-list"
      />
      <ToolbarButton
        icon={<ListOrdered className="h-4 w-4" />}
        label={t('floating.orderedList')}
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        testId="rich-text-fixed-ordered-list"
      />

      <Separator orientation="vertical" className="mx-1.5 h-4 opacity-40" />

      <LinkPopover editor={editor} />
    </div>
  );
}
